import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GROUPS: { title: string; items: [string, string][] }[] = [
  {
    title: "Anywhere",
    items: [
      ["Ctrl K", "Search records or run a command"],
      ["Ctrl J", "Open or close Ask AI"],
      ["?", "Show this guide"],
      ["Esc", "Close a panel or dialog"],
    ],
  },
  {
    title: "On a list (Leads, Deals, Accounts, Contacts and more)",
    items: [
      ["/", "Go to the search box"],
      ["n", "New record"],
      ["j  or  ↓", "Next row"],
      ["k  or  ↑", "Previous row"],
      ["Enter  or  o", "Quick look at the highlighted row"],
      ["x", "Select or unselect the highlighted row"],
      ["Esc", "Leave the highlighted row"],
    ],
  },
  {
    title: "Quick look panel",
    items: [
      ["Esc", "Close the panel"],
      ["Tab", "Move between Star, Open, Call and Email"],
    ],
  },
];

const typing = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
};

/**
 * One-key shortcuts for the lists: they do nothing while typing, with a dialog open, or with Ctrl / Alt / Meta held.
 * Rows are found in the page's table, so any list with a table (and a quick-look button in its rows) takes part.
 */
export function KeyboardShortcuts() {
  const { pathname } = useLocation();
  const [help, setHelp] = useState(false);

  // A new page starts with no row chosen
  useEffect(() => {
    document.querySelectorAll('tr[data-kbd-row="1"]').forEach((r) => r.removeAttribute("data-kbd-row"));
  }, [pathname]);

  // The user menu and anything else can ask for the guide
  useEffect(() => {
    const show = () => setHelp(true);
    window.addEventListener("crm:show-shortcuts", show);
    return () => window.removeEventListener("crm:show-shortcuts", show);
  }, []);

  useEffect(() => {
    const rows = () => Array.from(document.querySelectorAll<HTMLTableRowElement>("main table tbody tr")).filter((r) => r.querySelector("td") && r.offsetParent !== null);
    const current = () => document.querySelector<HTMLTableRowElement>('tr[data-kbd-row="1"]');
    const move = (delta: number) => {
      const list = rows();
      if (!list.length) return;
      const cur = current();
      const i = cur ? list.indexOf(cur) : -1;
      const next = list[Math.min(list.length - 1, Math.max(0, i + delta))]!;
      cur?.removeAttribute("data-kbd-row");
      next.setAttribute("data-kbd-row", "1");
      next.scrollIntoView({ block: "nearest" });
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (typing(e.target) || document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      const k = e.key;
      if (k === "?") {
        e.preventDefault();
        setHelp(true);
      } else if (k === "/") {
        const box = document.querySelector<HTMLInputElement>("[data-list-search]");
        if (box) {
          e.preventDefault();
          box.focus();
          box.select();
        }
      } else if (k === "n") {
        const add = document.querySelector<HTMLElement>("[data-new-record]");
        if (add) {
          e.preventDefault();
          add.click();
        }
      } else if (k === "j" || k === "ArrowDown") {
        if (k === "ArrowDown" && !current()) return; // arrows scroll the page until a row is chosen
        e.preventDefault();
        move(1);
      } else if (k === "k" || k === "ArrowUp") {
        if (k === "ArrowUp" && !current()) return;
        e.preventDefault();
        move(-1);
      } else if (k === "Enter" || k === "o") {
        const row = current();
        const eye = row?.querySelector<HTMLElement>('button[aria-label^="Quick look"]');
        if (eye) {
          e.preventDefault();
          eye.click();
        }
      } else if (k === "x") {
        const box = current()?.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (box) {
          e.preventDefault();
          box.click();
        }
      } else if (k === "Escape") {
        current()?.removeAttribute("data-kbd-row");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Dialog open={help} onOpenChange={setHelp}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Shortcuts work when you are not typing in a box. Press ? any time to open this guide.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.title}</h3>
              <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-sm">
                {g.items.map(([key, what]) => (
                  <React.Fragment key={g.title + key}>
                    <dt>
                      <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">{key}</kbd>
                    </dt>
                    <dd className="text-muted-foreground">{what}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
